import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Newspaper, ExternalLink, RefreshCw } from "lucide-react";

const REGIONS = [
  { key: "USA", flag: "🇺🇸" },
  { key: "UK", flag: "🇬🇧" },
  { key: "Nigeria", flag: "🇳🇬" },
];

function timeAgo(s) {
  const d = new Date(s);
  if (isNaN(d)) return "";
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 60) return `${Math.max(m, 1)}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

export default function WorldNews() {
  const [region, setRegion] = useState("USA");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/news", { params: { region } }).then((r) => setItems(r.data.items)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region]);

  return (
    <Card className="gold-ring glass p-5" data-testid="world-news">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Newspaper className="h-5 w-5 text-[hsl(var(--water))]" /><h3 className="font-display text-lg font-700">Live World News</h3></div>
        <div className="flex items-center gap-2">
          {REGIONS.map((r) => (
            <button key={r.key} onClick={() => setRegion(r.key)} data-testid={`news-region-${r.key}`}
              className={`rounded-full px-3 py-1 text-xs font-600 transition-colors ${region === r.key ? "bg-[hsl(var(--water))] text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {r.flag} {r.key}
            </button>
          ))}
          <button onClick={load} className="rounded-full p-1.5 text-muted-foreground hover:text-foreground" data-testid="news-refresh" aria-label="Refresh news"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      {loading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <div className="max-h-[420px] space-y-1 overflow-y-auto pr-1">
          {items.map((n, i) => (
            <a key={n.link || i} href={n.link} target="_blank" rel="noreferrer" data-testid={`news-item-${i}`}
              className="group flex gap-3 rounded-lg p-2.5 transition-colors hover:bg-secondary/70">
              {n.image ? <img src={n.image} alt="" className="h-16 w-24 shrink-0 rounded-md object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                : <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--water))]/10"><Newspaper className="h-5 w-5 text-[hsl(var(--water))]" /></div>}
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-600 group-hover:text-[hsl(var(--water))]">{n.title}</p>
                <p className="mt-1 text-[0.7rem] text-muted-foreground">{n.source}{n.published ? ` · ${timeAgo(n.published)}` : ""}</p>
              </div>
              <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
          ))}
          {items.length === 0 && <p className="text-sm text-muted-foreground">News is unavailable right now. Try refreshing shortly.</p>}
        </div>
      )}
    </Card>
  );
}
